import { useState, useEffect, useCallback } from 'react';

export default function useViewerGoals(channelName, enabled = true) {
  const [goalsData, setGoalsData] = useState(null);
  const [twitchGoals, setTwitchGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isJson = (res) => (res.headers.get('content-type') || '').includes('application/json');

    const fetchGoals = async () => {
      try {
        const [viewerResponse, twitchResponse] = await Promise.all([
          fetch(`/api/data?type=viewer-goals&channelName=${encodeURIComponent(channelName)}`),
          fetch('/api/data?type=twitch-goals')
        ]);

        if (viewerResponse.ok && isJson(viewerResponse)) {
          const data = await viewerResponse.json();
          setGoalsData(data);
        }

        if (twitchResponse.ok && isJson(twitchResponse)) {
          const twitchData = await twitchResponse.json();
          if (twitchData.success && twitchData.goals) {
            setTwitchGoals(twitchData.goals);
          }
        }
      } catch (err) {
        console.error('Error fetching goals:', err);
      } finally {
        setLoading(false);
      }
    };

    // Always fetch once so the panel shows real state, even when offline
    fetchGoals();

    // Stop polling when the stream is offline (`enabled` is false) so an idle
    // OBS overlay makes no requests.
    if (!enabled) return undefined;

    // Goals change rarely; 60s keeps the overlay fresh without burning
    // through Vercel's edge request quota (5s polling was ~35k requests/day)
    const interval = setInterval(fetchGoals, 60000);
    return () => clearInterval(interval);
  }, [channelName, enabled]);

  const getTwitchGoal = useCallback((type) => {
    return twitchGoals.find(g => g.type === type || g.type.includes(type));
  }, [twitchGoals]);

  const getSubGoal = useCallback(() => {
    if (goalsData?.subGoal && goalsData.subGoal.target > 0) {
      return {
        current: goalsData.subGoal.current || 0,
        target: goalsData.subGoal.target || 0,
        type: 'subscription'
      };
    }

    const types = ['subscription', 'new_subscription', 'subscription_count', 'new_subscription_count'];
    for (const type of types) {
      const goal = twitchGoals.find(g => g.type === type);
      if (goal) return goal;
    }
    return twitchGoals.find(g => g.type.toLowerCase().includes('sub'));
  }, [goalsData, twitchGoals]);

  const getFollowerGoal = useCallback(() => {
    if (goalsData?.followerGoal && goalsData.followerGoal.target > 0) {
      return {
        current: goalsData.followerGoal.current || 0,
        target: goalsData.followerGoal.target || 0,
        type: 'follower'
      };
    }
    return getTwitchGoal('follower');
  }, [goalsData, getTwitchGoal]);

  return { goalsData, twitchGoals, loading, getSubGoal, getFollowerGoal };
}
